/**
 * Type definitions for workspace real-time notifications
 *
 * This module defines the types for WebSocket notifications related to settings / system-control events.
 * The backend sends notifications using notify_system()
 */

/** System-wide event types for settings communication. */
export type SettingsCommunicationEventType =
  | 'maintenance_mode_enabled'
  | 'maintenance_mode_disabled'
  | 'maintenance_mode_scheduled'
  | 'maintenance_mode_cancelled'
  | 'feature_flag_updated'
  | 'system_operation_completed'
  | 'logout_user';

/**
 * Notification payload from backend via notify_system()
 */
export type SettingsCommunicationEvent = {
  /** The type of system event */
  event: SettingsCommunicationEventType;
  /** ISO timestamp when the event occurred */
  timestamp: string;
  /** ID of the user who initiated the event */
  initiating_user_id?: string | null;
  /** ID of the user targeted by the event, used when event is LOGOUT_USER to specify which user should be logged out */
  target_user_id?: string | null;
};

/** Connection state for the WebSocket connection.*/
export enum ConnectionState {
  /** Not connected and not attempting to connect */
  DISCONNECTED = 'DISCONNECTED',
  /** Attempting to establish connection */
  CONNECTING = 'CONNECTING',
  /** Successfully connected and subscribed */
  CONNECTED = 'CONNECTED',
  /** Connection failed or lost, attempting to reconnect */
  RECONNECTING = 'RECONNECTING',
  /** Permanent error, won't retry */
  ERROR = 'ERROR',
}

/** Configuration for WebSocket connection */
export type WebSocketConfig = {
  /** Maximum number of reconnection attempts */
  maxReconnectAttempts: number;
  /** Initial reconnection delay in milliseconds */
  reconnectDelayMs: number;
  /** Maximum reconnection delay in milliseconds (for exponential backoff) */
  maxReconnectDelayMs: number;
  /** Timeout for connection attempts in milliseconds */
  connectionTimeoutMs: number;
  /** Heartbeat interval in milliseconds */
  heartbeatIntervalMs: number;
};

/**
 * WebSocket connection info from API
 * Response from GET /settings/notifications/connect/{connection_str}/
 */
export type WebSocketConnectionInfo = {
  /** WebSocket URL with embedded access token */
  url: string;
};

/**
 * Subscription response from API
 * Response from POST /settings/notifications/subscribe/{connection_str}/
 */
export type SubscriptionResponse = {
  /** Human-readable status message */
  message: string;
  /** The user ID used for the WebSocket connection */
  user_id: string;
  /** The WebSocket group the user was added to (internal-workspace-{id} or external-workspace-{id}) */
  group: string;
  /** Error message if the operation partially failed */
  error: string;
};

/**
 * Azure Web PubSub system message
 * Sent by Azure Web PubSub for connection lifecycle events
 */
export type WebPubSubSystemMessage = {
  /** Message type identifier */
  type: 'system';
  /** System event type (connected, disconnected, etc.) */
  event: string;
  /** Optional data payload */
  [key: string]: unknown;
};

/**
 * Notification payload from backend via notify_system()
 */
export type SettingsCommunicationDataMesage = SettingsCommunicationEvent & {
  /** A given reason when the event type is one of (MAINTENANCE_MODE_ENABLED, MAINTENANCE_MODE_CANCELLED, MAINTENANCE_MODE_SCHEDULED) */
  reason?: string;
  /* Estimated end, used when event is MAINTENANCE_MODE_ENABLED */
  estimated_end?: string | null;
  /** Scheduled start time used when event is MAINTENANCE_MODE_SCHEDULED */
  scheduled_start?: string | null;
  /** Scheduled end time used when event is MAINTENANCE_MODE_SCHEDULED */
  scheduled_end?: string | null;
};

/**
 * Union type for all possible WebSocket messages
 * Azure Web PubSub can send either system messages or data messages
 */
export type WebSocketMessage =
  | WebPubSubSystemMessage
  | SettingsCommunicationDataMesage;

/**
 * Type guard to check if a message is a system message
 */
export function isSystemMessage(
  message: WebSocketMessage,
): message is WebPubSubSystemMessage {
  return 'type' in message && message.type === 'system';
}

/**
 * Type guard to check if a message is a data message.
 */
export function isDataMessage(
  message: WebSocketMessage,
): message is SettingsCommunicationDataMesage {
  return 'event' in message && !('type' in message);
}

export type NotificationSetting = {
  id: string;
  mode: string;
  email_template: {
    id: string;
    subject: string;
    body: string;
  };
  sub_category_id: string;
  requirement: string;
  in_app_title: string;
  in_app_text: string;
  email_template_id: string;
};
